import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { Observable } from 'rxjs';
import { Placement, Screen } from '..';
import { BrokenScreensResponse } from '@admin/start/start.models';
import { PaginationResult, PaginatorParams } from '@api/models/paginator';
import { UnusedScreen } from '@admin/start/start.models';
import { IdName } from '@api/models/idname';
import { DeviceServiceStateHistoryResult, UpdateAppsVersionRequest, UpdateSmsNotificationsRequest } from '@app/admin/screen/model/device.model';
import { DeviceReportFilters } from '@admin/screen/device-reports/device-reports.model';
import { Widget, WidgetSetup } from '@app/admin/widgets/widgets.model';
import { ResetRegistrationResult, SaveSensorSettingsRequest, ShowVideoFeedResponse } from '@app/admin/screen/screen.model';

@Injectable({
  providedIn: 'root',
})
export class ScreenApi extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  static readonly GetScreensPath = '/screens';

  getScreens(params?) {
    return this.http.get<any>(`${this.rootUrl}${ScreenApi.GetScreensPath}`, params ? { params } : {});
  }

  getScreenById(screenId, params) {
    return this.http.get<Screen>(`${this.rootUrl}${ScreenApi.GetScreensPath}/${screenId}`, { params });
  }

  editScreen(screen): Observable<any> {
    return this.http.put(`${this.rootUrl}${ScreenApi.GetScreensPath}`, screen);
  }

  updateAppsVersion(request: UpdateAppsVersionRequest) {
    return this.http.put(`${this.rootUrl}${ScreenApi.GetScreensPath}/apps-version`, request);
  }

  updateSmsNotifications(request: UpdateSmsNotificationsRequest): Observable<string[]> {
    return this.http.put<string[]>(`${this.rootUrl}${ScreenApi.GetScreensPath}/sms-notifications`, request);
  }

  saveScreen(screen): Observable<any> {
    return this.http.post(`${this.rootUrl}${ScreenApi.GetScreensPath}`, screen);
  }

  deleteScreen(screenId: number, currentLocalDate: string) {
    return this.http.delete(`${this.rootUrl}${ScreenApi.GetScreensPath}/${screenId}?currentLocalDate=${currentLocalDate}`);
  }

  getUnusedScreens(params): Observable<PaginationResult<UnusedScreen>> {
    return this.http.get<PaginationResult<UnusedScreen>>(`${this.rootUrl}${ScreenApi.GetScreensPath}/unused`, { params });
  }

  getBrokenScreens(params): Observable<any> {
    return this.http.get<BrokenScreensResponse>(`${this.rootUrl}${ScreenApi.GetScreensPath}/issues`, { params });
  }

  getUniquePlacements(params?): Observable<any> {
    return this.http.get<Array<Placement>>(`${this.rootUrl}${ScreenApi.GetScreensPath}/placements`, { params });
  }

  getBookPlacements(params?): Observable<any> {
    return this.http.get<Array<Placement>>(`${this.rootUrl}${ScreenApi.GetScreensPath}/book/placements`, { params });
  }

  advertiserVersions(): Observable<any> {
    return this.http.get<string[]>(`${this.rootUrl}${ScreenApi.GetScreensPath}/advertiser-versions`);
  }

  aiVersions(): Observable<any> {
    return this.http.get<string[]>(`${this.rootUrl}${ScreenApi.GetScreensPath}/ai-versions`);
  }

  activateScreen(screenId: number, isActive) {
    return this.http.put(`${this.rootUrl}${ScreenApi.GetScreensPath}/${screenId}/status`, isActive);
  }

  getPlacementsForBookingFilter(customerIds: number[]): Observable<IdName[]> {
    return this.http.get<IdName[]>(`${this.rootUrl}${ScreenApi.GetScreensPath}/bookings-placements`, { params: { customerIds } });
  }

  resetRegistration(screenId: number, serialNumber?: string): Observable<void> {
    const params: Record<string, string | number> = { screenId };
    if (serialNumber) {
      params['serialNumber'] = serialNumber;
    }
    return this.http.delete<void>(`${this.rootUrl}${ScreenApi.GetScreensPath}/reset-nuc`, { params });
  }

  getManagerVersions(): Observable<string[]> {
    return this.http.get<string[]>(`${this.rootUrl}${ScreenApi.GetScreensPath}/manager-versions`);
  }

  getWidgets(params): Observable<PaginationResult<Widget[]>> {
    return this.http.get<PaginationResult<Widget[]>>(`${this.rootUrl}${ScreenApi.GetScreensPath}/widgets`, { params });
  }

  getWidgetSetup(widgetId: number): Observable<Widget> {
    return this.http.get<Widget>(`${this.rootUrl}${ScreenApi.GetScreensPath}/widget-setup`, { params: { widgetId } });
  }

  createWidgetSetup(widgetSetup: FormData): Observable<Widget> {
    return this.http.post<Widget>(`${this.rootUrl}${ScreenApi.GetScreensPath}/widget-setup`, widgetSetup);
  }

  updateWidgetSetup(widgetSetup: FormData): Observable<Widget> {
    return this.http.put<Widget>(`${this.rootUrl}${ScreenApi.GetScreensPath}/widget-setup`, widgetSetup);
  }

  deleteWidgetSetup(widgetId: number): Observable<void> {
    return this.http.delete<void>(`${this.rootUrl}${ScreenApi.GetScreensPath}/widget-setup`, { params: { widgetId } });
  }

  getScreenOptions(customerId: number, widgetId?: number): Observable<IdName[]> {
    const params = { customerId, ...(widgetId && { includeForWidgetId: widgetId }) };
    return this.http.get<IdName[]>(`${this.rootUrl}${ScreenApi.GetScreensPath}/widget-setup/screen-options`, { params });
  }

  getScreenGroupOptions(customerId: number, widgetId?: number): Observable<IdName[]> {
    const params = { customerId, ...(widgetId && { includeForWidgetId: widgetId }) };
    return this.http.get<IdName[]>(`${this.rootUrl}${ScreenApi.GetScreensPath}/widget-setup/screen-groups-options`, { params });
  }

  enableWidget(widgetId: number, isEnabled: boolean): Observable<void> {
    return this.http.put<void>(`${this.rootUrl}${ScreenApi.GetScreensPath}/widget-setup/isEnabled`, { id: widgetId, isEnabled });
  }

  // GET /screens/config-settings-history
  getRegistrationHistory(deviceId: number, paginatorParams?: PaginatorParams): Observable<PaginationResult<ResetRegistrationResult>> {
    const params = { deviceId, ...paginatorParams };
    return this.http.get<PaginationResult<ResetRegistrationResult>>(`${this.rootUrl}${ScreenApi.GetScreensPath}/config-settings-history`, { params });
  }

  getServiceStateHistory(
    filters: DeviceReportFilters,
    currentLocalDate: string,
    paginatorParams?: PaginatorParams
  ): Observable<PaginationResult<DeviceServiceStateHistoryResult>> {
    return this.http.post<PaginationResult<DeviceServiceStateHistoryResult>>(
      `${this.rootUrl}${ScreenApi.GetScreensPath}/service-state-history`,
      { ...filters, ...paginatorParams, currentLocalDate },
    );
  }

  downloadServiceStateHistory(filters: DeviceReportFilters, currentLocalDate: string): Observable<Blob> {
    return this.http.post(
      `${this.rootUrl}${ScreenApi.GetScreensPath}/service-state-history/download`,
      { ...filters, currentLocalDate },
      { responseType: 'blob' },
    );
  }

  createSensorSettings(screenId: number, request: SaveSensorSettingsRequest): Observable<void> {
    return this.http.post<void>(`${this.rootUrl}${ScreenApi.GetScreensPath}/${screenId}/sensor-settings`, request);
  }

  updateSensorSettings(screenId: number, request: SaveSensorSettingsRequest): Observable<void> {
    return this.http.put<void>(`${this.rootUrl}${ScreenApi.GetScreensPath}/${screenId}/sensor-settings`, request);
  }

  showVideoFeed(screenId: number): Observable<ShowVideoFeedResponse> {
    return this.http.post<ShowVideoFeedResponse>(`${this.rootUrl}${ScreenApi.GetScreensPath}/${screenId}/show-video-feed`, {});
  }
}
